import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { AuthService } from '@brightside/core/auth';
import {
  trigger,
  transition,
  style,
  animate,
} from '@angular/animations';
import { MenuComponent } from '../menu/menu.component';
import { RippleModule } from 'primeng/ripple';
import { Avatar } from 'primeng/avatar';
import { TieredMenu } from 'primeng/tieredmenu';
import { MenuItem as PrimeMenuItem } from 'primeng/api';
import { OmniSearchComponent } from '@brighthub/search';
import { NgClass } from '@angular/common';
import {
  MenuItem,
  MenuKey,
  MenuStore,
} from '@brighthub/shell/data-access-menu';

@Component({
  selector: 'bhub-shell',
  standalone: true,
  imports: [
    RippleModule,
    RouterModule,
    MenuComponent,
    Avatar,
    OmniSearchComponent,
    NgClass,
    TieredMenu,
  ],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-in', style({ opacity: 1 })),
      ]),
    ]),
  ],
})
export class ShellComponent {
  private _auth = inject(AuthService);
  private _menuStore = inject(MenuStore);

  readonly settingsMenu = viewChild<TieredMenu>('settingsMenu');

  readonly userDisplayName = this._auth.userDisplayName;
  isSidebarCollapsed = signal(true);
  menuItems: Signal<MenuItem[]> = this._menuStore.menu(MenuKey.MAIN);
  settingsItems: Signal<MenuItem[]> = this._menuStore.menu(MenuKey.SETTINGS);
  displayMenu = signal<boolean>(false);

  /** Top-bar product name (rebranded from BrightNav). */
  readonly brandName = signal('BrightNav');

  /**
   * Gear popup: configured {@link MenuKey.SETTINGS} entries (group-filtered by MenuStore)
   * plus shell-owned Sign Out. Sidebar entries stay on {@link MenuKey.MAIN}.
   */
  readonly settingsMenuItems = computed((): PrimeMenuItem[] => [
    ...this.settingsItems().map((item) => this.toPrimeMenuItem(item)),
    {
      label: 'Sign Out',
      icon: 'pi pi-sign-out',
      command: () => this.signOut(),
    },
  ]);

  constructor() {
    effect(() => {
      const menuItems = this.menuItems();
      if (menuItems && menuItems.length >= 2) {
        this.displayMenu.set(true);
      }
    });
  }

  toggleSidebarCollapse(): void {
    this.isSidebarCollapsed.update((collapsed) => !collapsed);
  }

  toggleSettingsMenu(event: Event): void {
    this.settingsMenu()?.toggle(event);
  }

  signOut(): void {
    this._auth.signOut();
  }

  private toPrimeMenuItem(item: MenuItem): PrimeMenuItem {
    const children = item.children?.map((child) => this.toPrimeMenuItem(child));
    return {
      label: item.label,
      icon: this.toPrimeIcon(item.icon),
      routerLink: item.route,
      items: children?.length ? children : undefined,
    };
  }

  /** MenuStore icons are stored as `pi-cog`; PrimeNG expects `pi pi-cog`. */
  private toPrimeIcon(icon: string | undefined): string | undefined {
    if (!icon) {
      return undefined;
    }
    return icon.startsWith('pi ') ? icon : `pi ${icon}`;
  }
}
